import React, { useCallback, useContext, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { Button, Tooltip, Table, message, TablePaginationConfig, Modal } from 'antd';
import { EditOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useTranslation } from 'react-i18next';
import { ProductNameColumn, ShowHideColumn } from './column';
import { PRODUCT } from '../../../constants/define/translation';
import { MESSAGE_ERROR } from '../../../constants/message';
import { appStyles } from '../../../styles';
import { IndexColumn } from '../shared-column';
import {
  postSortProductCatalogsRepository,
  ProductCatalog,
  ProductCatalogStatus,
} from '../../../repository/mytm/shop-product/shopProductRepository';
import { ConfirmAction } from '../../molecules/confirm/ConfirmAction';
import { postLoginRepository } from '../../../repository/mytm/auth/loginRepository';
import { UserContext } from '../../../repository/context/userContext';

const { Column } = Table;

interface ProductOrdersTableProps {
  records: ProductCatalog[];
  loading?: boolean;
  className?: string;
  onRefresh: () => void;
  setIsOpenModal: (boolean: Boolean) => void;
  setDataRecord: Function;
  pagination?: TablePaginationConfig;
  countrySelected: string;
  setLoading: (value: boolean) => void;
}

const DraggableBodyRow = ({ index, moveRow, className, style, ...restProps }: any) => {
  const ref = useRef();
  const type = 'DraggableBodyRow';

  const [{ isOver, dropClassName }, drop] = useDrop({
    accept: type,
    collect: (monitor: any) => {
      const { index: dragIndex } = monitor.getItem() || {};
      if (dragIndex === index) {
        return {};
      }
      return {
        isOver: monitor.isOver(),
        dropClassName: dragIndex < index ? ' drop-over-downward' : ' drop-over-upward',
      };
    },
    drop: (item: any) => {
      moveRow(item.index, index);
    },
  });
  const [, drag] = useDrag({
    type,
    item: { index },
    collect: (monitor: any) => ({
      isDragging: monitor.isDragging(),
    }),
  });
  drop(drag(ref));
  return (
    <tr
      ref={ref}
      className={`${className}${isOver ? dropClassName : ''}`}
      style={{ cursor: 'move', ...style }}
      {...restProps}
    />
  );
};

export const ProductCatalogTable = (props: ProductOrdersTableProps) => {
  const { t } = useTranslation(PRODUCT);
  const [isLoading, setIsLoading] = useState(false);
  const userContext = useContext(UserContext);
  const { records, loading, className, onRefresh, pagination, countrySelected, setLoading } = props;
  const components: any = {
    body: {
      row: DraggableBodyRow,
    },
  };

  const recordActive = records.filter(val => val.status === ProductCatalogStatus.ACTIVE);

  const moveRow = useCallback(
    (dragIndex: number, hoverIndex: number) => {
      setIsLoading(true);
      const dragRecordsList = [...records];
      const dragData = dragRecordsList[dragIndex];
      dragRecordsList.splice(dragIndex, 1);
      dragRecordsList.splice(hoverIndex, 0, dragData);

      postSortProductCatalogsRepository({
        sortOrderList: dragRecordsList.map((record, index) => ({
          id: record.id,
          productId: record.product_id,
          sortOrder: index + 1,
        })),
        country: countrySelected,
      })
        .then(() => {
          setIsLoading(false);
          onRefresh();
        })
        .catch(() => {
          message.error(MESSAGE_ERROR.M02);
          setIsLoading(false);
        });
    },
    [records, onRefresh],
  );

  const handleEdit = (record: ProductCatalog) => {
    Modal.confirm({
      title: t('popup.title'),
      okText: t('popup.okBtn'),
      cancelText: t('popup.cancelBtn'),
      icon: <ExclamationCircleOutlined />,
      content: (
        <ConfirmAction
          textConfirmAction={t('popup.edit')}
          isConfirmPassword
          textConfirmPassword={t('popup.confirmPassword')}
        />
      ),
      onOk() {
        setLoading && setLoading(true);
        const inputPasswordNode = document.getElementById('inputPasswordForConfirm');
        const password = (inputPasswordNode as any).value;
        if (!userContext.user?.email || userContext.user?.email === '') {
          setLoading && setLoading(false);
          message.error(MESSAGE_ERROR.M02);
        } else {
          postLoginRepository({
            email: userContext.user?.email,
            password,
          })
            .then(() => {
              props.setDataRecord(record);
              props.setIsOpenModal(true);
              setLoading && setLoading(false);
            })
            .catch((e: any) => {
              message.error(e?.errorMessage);
              setLoading(false);
            });
        }
      },
      onCancel() {
        // NOT
      },
    });
  };

  return (
    <>
      <DndProvider backend={HTML5Backend}>
        <StyledProductTable
          dataSource={records}
          loading={loading || isLoading}
          className={className}
          rowKey="index"
          components={components}
          pagination={pagination}
          onRow={(recordDeep: any, index?: number): any => {
            return {
              index,
              moveRow,
            };
          }}
        >
          <Column
            title={t('table.title.index')}
            align="center"
            dataIndex="index"
            key="index"
            render={value => <IndexColumn value={value} />}
            width="60px"
          />
          <Column
            title={t('table.title.show')}
            align="center"
            width={100}
            dataIndex="show"
            key="show"
            render={(_, record: ProductCatalog) => (
              <ShowHideColumn countRecord={recordActive.length} onRefresh={onRefresh} record={record} />
            )}
          />
          <Column
            title={t('table.title.title')}
            align="left"
            dataIndex="productName"
            key="productName"
            render={(value: any, record: ProductCatalog) => <ProductNameColumn product={record} />}
            width="50%"
          />
          <Column
            title={t('table.title.action')}
            align="left"
            key="action"
            width={100}
            render={(_, record: ProductCatalog) => (
              <Tooltip title={t('table.tooltip.edit')}>
                <Button icon={<EditOutlined />} onClick={() => handleEdit(record)} />
              </Tooltip>
            )}
          />
        </StyledProductTable>
      </DndProvider>
    </>
  );
};

const StyledProductTable = styled(Table)`
  .ant-table-thead {
    border-bottom: 2px solid #eee;
    & > th > tr {
      ${appStyles.background.tableBgGray}
      text-transform: uppercase;
      font-size: 0.75rem;
      font-weight: 600;
      border: 1px solid #dededee3;
      &:before {
        display: none;
      }
    }
  }

  .ant-pagination-item a {
    line-height: 30px;
  }

  .card-name-tiles {
    max-width: 180px;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    line-height: 21px;
    max-height: 48px;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }

  & th.action-home {
    text-align: center !important;
  }
`;
